import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { joinUrl } from './params';
import type { QueryParams } from './params';

// CRUD factory bound to one client + one collection path. Methods unwrap `response.data`;
// reach for the raw client when you need headers or the full response.

export interface Repo<T> {
  path: string;
  client: AxiosInstance;
  fetch<R = T[]>(params?: QueryParams, config?: AxiosRequestConfig): Promise<R>;
  fetchOne<R = T>(id: string | number, params?: QueryParams, config?: AxiosRequestConfig): Promise<R>;
  count(params?: QueryParams, config?: AxiosRequestConfig): Promise<number>;
  create<R = T>(data: unknown, config?: AxiosRequestConfig): Promise<R>;
  update<R = T>(id: string | number, data: unknown, config?: AxiosRequestConfig): Promise<R>;
  remove<R = T>(id: string | number, config?: AxiosRequestConfig): Promise<R>;
}

export function Repository<T = unknown>(client: AxiosInstance, path: string): Repo<T> {
  const unwrap = <R>(request: Promise<AxiosResponse<unknown>>): Promise<R> =>
    request.then((res) => res.data as R);

  const get = <R>(url: string, params?: QueryParams, config?: AxiosRequestConfig): Promise<R> =>
    unwrap<R>(client.get(url, { ...config, params }));

  return {
    path,
    client,
    fetch: <R = T[]>(params?: QueryParams, config?: AxiosRequestConfig) =>
      get<R>(path, params, config),

    fetchOne: <R = T>(id: string | number, params?: QueryParams, config?: AxiosRequestConfig) =>
      get<R>(joinUrl(path, String(id)), params, config),

    count: (params?: QueryParams, config?: AxiosRequestConfig) =>
      get<number>(joinUrl(path, 'count'), params, config).then((value) => Number(value) || 0),

    create: <R = T>(data: unknown, config?: AxiosRequestConfig): Promise<R> =>
      unwrap<R>(client.post(path, data, config)),

    update: <R = T>(id: string | number, data: unknown, config?: AxiosRequestConfig): Promise<R> =>
      unwrap<R>(client.put(joinUrl(path, String(id)), data, config)),

    remove: <R = T>(id: string | number, config?: AxiosRequestConfig): Promise<R> =>
      unwrap<R>(client.delete(joinUrl(path, String(id)), config)),
  };
}

export default Repository;
